// ---------------------------------------------------------------------------
// calendarOverlay
//
// Shared workaround for uikit's DatePicker inside one of our Dialogs.
//
// uikit bundles its own copy of Radix, so a DatePicker's calendar portal shares
// no layer or focus state with the Dialog it is rendered inside. Two
// consequences every such Dialog has to handle, and which these two helpers
// exist to support:
//
//   1. The Dialog must be `modal={false}`, or Radix's `pointer-events: none` on
//      <body> leaves the calendar's day cells unclickable.
//   2. Escape would otherwise dismiss BOTH layers at once, taking the whole
//      dialog down along with the calendar — so `onEscapeKeyDown` has to check
//      for an open calendar first and close only that.
//
// Lives in `lib/` rather than inside a feature because it is consumed from more
// than one: any dialog hosting a uikit DatePicker needs it.
// ---------------------------------------------------------------------------

/**
 * Reports whether a uikit DatePicker calendar is currently open.
 *
 * The calendar lives in its own portal at the end of `document.body`, so there is no
 * React-side signal for it; the day grid uikit renders inside the popover is the only
 * marker that tells it apart from any other popover on the page.
 * @returns {boolean} True while a calendar overlay is mounted.
 */
export function isCalendarOverlayOpen(): boolean {
  return document.querySelector('[data-slot="popover-content"] [data-slot="calendar"]') !== null;
}

/**
 * Closes an open uikit DatePicker calendar.
 *
 * uikit keeps the popover's open state to itself, and its Radix copy is a different
 * module instance than the Dialog's, so the only handle we have on it is the
 * pointer-down-outside dismissal its own layer listens for. Dispatching that on the
 * body reaches the calendar without touching the Dialog, whose `onInteractOutside`
 * already refuses to dismiss.
 */
export function closeCalendarOverlay() {
  document.body.dispatchEvent(new Event("pointerdown", { bubbles: true, cancelable: true }));
}
